/**
 * Générateur — Il réalise des activités d'algorithmique débranchée.
 * Attendu ID : 244 | Niveau : 3ème | Domaine : Algorithmique (d21, O1 niv. 1)
 *
 * ⚠️ BANQUE 3e : le doc prof ne couvre que le niveau 3 (brevet) — attendu
 * généré depuis le libellé, sur le modèle du robot débranché 4e (option (a)
 * du TODO §5, précédent 4e, à faire valider par l'utilisatrice). Plan
 * auto-validé 2026-07-18 : « robot » (exécuter une séquence AVANCE/PIVOTE
 * avec une boucle « répète » à déplier À LA MAIN, arrivée en QCM),
 * « main » (dérouler un algorithme de calcul à la main, étapes en trous).
 */

import { randomChoice, randInt, makeQcm, makeTexteTrou } from '../../../utils';
import { imgElement, svgQuadrillage, executerRobot, deplierBoucle } from '../../4eme/algorithmique/algo';

const ATTENDU_ID = 244;
const L_ROBOT = 'Le robot débranché (avec une boucle)';
const L_MAIN = 'Dérouler un algorithme à la main';
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ═════════ « robot » ═════════

const genRobotQcm = () => {
    const depart = { c: randInt(2, 3), l: randInt(2, 3) };
    const n = randInt(2, 3);
    const corps = randomChoice([
        ['AVANCE', 'PIVOTE À GAUCHE', 'AVANCE', 'PIVOTE À DROITE'],
        ['AVANCE', 'AVANCE', 'PIVOTE À GAUCHE'],
        ['AVANCE', 'PIVOTE À GAUCHE', 'AVANCE'],
    ]);
    const res = executerRobot(depart, deplierBoucle(n, corps));
    const aff = (p) => `(${p.c} ; ${p.l})`;
    const distracteurs = [...new Set([
        aff(executerRobot(depart, corps).fin),               // une seule itération
        aff({ c: depart.c, l: depart.l }),                   // pas bougé
        aff({ c: res.fin.c + 1, l: res.fin.l }),
        aff({ c: res.fin.c, l: res.fin.l + 1 }),
    ])].filter(x => x !== aff(res.fin)).slice(0, 3);
    const payload = makeQcm([aff(res.fin), ...distracteurs], aff(res.fin),
        `<p>Un robot est posé sur la case (${depart.c} ; ${depart.l}) du quadrillage, ORIENTÉ VERS L'EST, `
        + 'c\'est-à-dire vers la droite. « PIVOTE À DROITE » = quart de tour horaire, sur place.</p>'
        + `<p><code>répète ${n} fois [${corps.map(i => i.toLowerCase()).join(' ; ')}]</code></p>`
        // « Déplie la boucle à la main » donnait la démarche d'exécution.
        + '<p><b>Sur quelle case (colonne ; ligne) le robot termine-t-il ?</b></p>',
        opts(L_ROBOT));
    payload.elements_additionnels = imgElement(svgQuadrillage({
        cols: 7, rows: 6, depart, orientation: 'est',
        desc: `machine|robot|depart=${depart.c};${depart.l}|repete=${n}|corps=${corps.join(',')}`,
    }));
    payload._meta.params = { depart, n, corps, fin: res.fin };
    return payload;
};

// ═════════ « main » ═════════

const genMainTrous = () => {
    let n0; let a; let b; let e1; let e2; let e3;
    for (let essai = 0; essai < 50; essai++) {
        n0 = randInt(3, 9);
        a = randInt(2, 4);
        b = randInt(3, 11);
        e1 = n0 * a;                                        // étape 1 : multiplier par a
        e2 = e1 - b;                                        // étape 2 : retrancher b
        e3 = e2 * 2;                                        // étape 3 : doubler
        if (e2 > 0 && e1 !== e2 && e2 !== e3 && e1 !== e3) break;
    }
    const phrase = `Après l'étape 1 : {{${e1}}} ; après l'étape 2 : {{${e2}}} ; résultat final : {{${e3}}}.`;
    const payload = makeTexteTrou(phrase,
        '<p>Algorithme, à dérouler SANS machine :</p>'
        + `<p><code>1. Multiplier le nombre de départ par ${a}. 2. Retrancher ${b}. `
        + '3. Doubler le résultat.</code></p>'
        + `<p><b>Déroule l'algorithme pour le nombre de départ ${n0}.</b></p>`,
        { ...opts(L_MAIN), mode: 'saisie' });
    payload._meta.params = { n0, a, b, e1, e2, e3 };
    return payload;
};

const GENERATORS = {
    robot_qcm_unique: genRobotQcm,
    main_texte_trou_saisie: genMainTrous,
};
const FORMAT_LABELS = { qcm_unique: 'QCM', texte_trou_saisie: 'Saisie trous' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));
export const CATEGORIES = [
    { id: 'robot', label: L_ROBOT, formatsList: buildFormats('robot') },
    { id: 'main', label: L_MAIN, formatsList: buildFormats('main') },
];
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
